// Pendaftaran Mitra/Reseller mandiri (mig 3810) — helper murni & tipe.
//
// Alur: pelanggan umum (tanpa grup / slot 0) memilih level tujuan di /mitra →
// memenuhi syarat top-up minimal level itu (atau GRATIS) → admin approve
// top-up di /admin/saldo → level naik otomatis (trigger DB). Reseller yang
// sudah ber-level memakai jalur yang SAMA untuk naik ke slot lebih tinggi.
// Tanpa Supabase di sini supaya mudah diuji (idiom siteSettings.ts).

import {
  renderWaTemplate,
  resolveWaTemplate,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";
import { formatRupiah } from "@/lib/resellerLevels";
import { formatIndoDate } from "@/lib/platformLanding";

export type MitraLevelOption = {
  groupId: string;
  slot: number;
  name: string;
  discountPersen: number;
  registrationEnabled: boolean;
  /** Syarat top-up minimal (Rp). 0 = GRATIS (langsung naik level). */
  minTopup: number;
};

export type MitraApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled";

export type MitraPendingApplication = {
  id: string;
  targetGroupName: string;
  targetGroupSlot: number;
  minTopup: number;
  isFree: boolean;
  status: MitraApplicationStatus;
  topupRequestId: string | null;
  createdAt: string | null;
};

export type MitraOptions = {
  /** Status akun pelanggan (active/pending/…) — apply hanya utk active. */
  customerStatus: string;
  /** Slot level saat ini; 0 = pelanggan umum tanpa grup. */
  currentSlot: number;
  currentGroupName: string | null;
  /**
   * Masa aktif keanggotaan level SEKARANG (mig 3820): null = level ini tak
   * memakai masa aktif (tidak pernah turun otomatis).
   */
  demoteAfterMonths: number | null;
  /**
   * Perkiraan tanggal keanggotaan berakhir (YYYY-MM-DD, WIB) bila TIDAK ada
   * transaksi baru — rumus SAMA dgn sweep `demote_inactive_resellers`; belanja
   * apa pun menggesernya maju. null = level tanpa masa aktif.
   */
  membershipDemoteAt: string | null;
  pendingApplication: MitraPendingApplication | null;
  /** Semua grup AKTIF toko (urut slot), termasuk yang tak menerima pendaftaran. */
  levels: MitraLevelOption[];
};

function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/** Koersi defensif hasil RPC `reseller_registration_options` (jsonb). */
export function mapMitraOptions(raw: unknown): MitraOptions | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;

  const levelsRaw = Array.isArray(o.levels) ? o.levels : [];
  const levels: MitraLevelOption[] = levelsRaw
    .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
    .map((l) => ({
      groupId: str(l.groupId) ?? "",
      slot: Math.trunc(num(l.slot)),
      name: str(l.name) ?? `Level ${Math.trunc(num(l.slot))}`,
      discountPersen: num(l.discountPersen),
      registrationEnabled: l.registrationEnabled === true,
      minTopup: Math.max(0, Math.trunc(num(l.minTopup))),
    }))
    .filter((l) => l.groupId !== "" && l.slot > 0);

  let pending: MitraPendingApplication | null = null;
  const p = o.pendingApplication;
  if (p && typeof p === "object") {
    const pr = p as Record<string, unknown>;
    const id = str(pr.id);
    if (id) {
      const status = str(pr.status);
      pending = {
        id,
        targetGroupName: str(pr.targetGroupName) ?? "Level tujuan",
        targetGroupSlot: Math.trunc(num(pr.targetGroupSlot)),
        minTopup: Math.max(0, Math.trunc(num(pr.minTopup))),
        isFree: pr.isFree === true,
        status:
          status === "approved" ||
          status === "rejected" ||
          status === "cancelled"
            ? status
            : "pending",
        topupRequestId: str(pr.topupRequestId),
        createdAt: str(pr.createdAt),
      };
    }
  }

  const demoteMonthsRaw = Math.trunc(num(o.demoteAfterMonths, Number.NaN));
  const demoteAtRaw = str(o.membershipDemoteAt);

  return {
    customerStatus: str(o.customerStatus) ?? "",
    currentSlot: Math.max(0, Math.trunc(num(o.currentSlot))),
    currentGroupName: str(o.currentGroupName),
    demoteAfterMonths:
      Number.isFinite(demoteMonthsRaw) && demoteMonthsRaw >= 1
        ? demoteMonthsRaw
        : null,
    membershipDemoteAt:
      demoteAtRaw && /^\d{4}-\d{2}-\d{2}$/.test(demoteAtRaw)
        ? demoteAtRaw
        : null,
    pendingApplication: pending,
    levels,
  };
}

/**
 * Label "Keanggotaan aktif s.d. …" utk kartu level sekarang di /mitra.
 * null = level tanpa masa aktif → tidak ada baris yang perlu dirender.
 */
export function membershipActiveUntilLabel(
  membershipDemoteAt: string | null
): string | null {
  if (!membershipDemoteAt) return null;
  return `Keanggotaan aktif s.d. ${formatIndoDate(membershipDemoteAt)} — belanja apa pun memperpanjang otomatis.`;
}

/**
 * Level yang BISA didaftar pelanggan ini: menerima pendaftaran DAN slot lebih
 * tinggi dari level sekarang (naik saja, tak pernah turun — cermin guard RPC
 * `apply_reseller_upgrade` & mesin auto-promote mig 094), urut slot naik.
 */
export function eligibleUpgradeLevels(
  options: Pick<MitraOptions, "currentSlot" | "levels">
): MitraLevelOption[] {
  return options.levels
    .filter((l) => l.registrationEnabled && l.slot > options.currentSlot)
    .sort((a, b) => a.slot - b.slot);
}

/** Level ber-syarat 0 = gratis (langsung aktif tanpa menunggu admin). */
export function isFreeMitraLevel(minTopup: number): boolean {
  return minTopup <= 0;
}

/**
 * Level yang dipilih OTOMATIS di kartu "1 · Pilih Level Mitra": level tepat di
 * atas level sekarang, yaitu `eligible[0]` (`eligibleUpgradeLevels` sudah urut
 * slot naik). Tujuannya pelanggan yang cuma mau naik satu tingkat tak perlu
 * mengetuk kartu dulu — nominal top-up ikut ter-prefill.
 *
 * Pulangkan null = JANGAN ubah pilihan: daftar kosong, atau pilihan sekarang
 * masih ada di daftar (pilihan manual pelanggan tidak pernah ditimpa). Kalau
 * level terpilih hilang dari daftar (admin menutup pendaftarannya, level naik
 * lewat tab lain), jatuh kembali ke level teratas yang tersisa.
 */
export function resolveDefaultMitraLevel(
  eligible: MitraLevelOption[],
  selectedGroupId: string,
  preferredGroupId: string | null = null
): MitraLevelOption | null {
  if (eligible.length === 0) return null;
  if (eligible.some((l) => l.groupId === selectedGroupId)) return null;
  // Level yang dipilih pembeli di /daftar (langkah 1) menang atas "tepat di
  // atas level sekarang" — asalkan masih bisa didaftar. Id asing/tertutup
  // diabaikan, bukan dipercaya.
  const preferred = preferredGroupId
    ? eligible.find((l) => l.groupId === preferredGroupId)
    : undefined;
  return preferred ?? eligible[0];
}

/** Label syarat pendaftaran satu level. */
export function mitraRequirementLabel(minTopup: number): string {
  return isFreeMitraLevel(minTopup)
    ? "GRATIS"
    : `Top-up saldo min. ${formatRupiah(minTopup)}`;
}

/**
 * Parse input nominal top-up. WAJIB ada minimal satu DIGIT — jangan pernah
 * memulangkan 0 untuk teks tanpa angka (jebakan `parseRupiah`: "abc" → buang
 * non-digit → Number("") = 0; di kolom uang, salah ketik yang lolos jadi Rp 0
 * pernah jadi bug nyata — lihat platformPricing.ts).
 */
export function parseTopupAmountInput(text: string): number | null {
  if (!/\d/.test(text)) return null;
  const n = Number(text.replace(/[^\d]/g, ""));
  return Number.isFinite(n) ? n : null;
}

export type MitraSubmission = {
  level: MitraLevelOption | null;
  /** null = input nominal tak berisi angka. Diabaikan utk level gratis. */
  amount: number | null;
  /** Sudah memilih metode pembayaran? (Tunai termasuk memilih.) */
  methodChosen: boolean;
  isTunai: boolean;
  hasProof: boolean;
  /** Punya alamat utama tersimpan (syarat alur pendaftaran). */
  hasPrimaryAddress: boolean;
};

/** Validasi sebelum kirim — pulangkan pesan Indonesia, null = lolos. */
export function validateMitraSubmission(input: MitraSubmission): string | null {
  if (!input.level) return "Pilih level mitra yang ingin didaftar.";
  if (!input.hasPrimaryAddress) {
    return "Isi alamat utama kamu dulu di bagian Alamat.";
  }
  if (isFreeMitraLevel(input.level.minTopup)) return null;

  if (input.amount === null) return "Nominal top-up wajib diisi.";
  if (input.amount < input.level.minTopup) {
    return `Nominal top-up minimal ${formatRupiah(input.level.minTopup)} untuk level ${input.level.name}.`;
  }
  if (!input.methodChosen) return "Pilih metode pembayaran.";
  if (!input.isTunai && !input.hasProof) {
    return "Bukti transfer wajib diunggah.";
  }
  return null;
}

/**
 * Banner/kartu ajakan "Daftar Mitra" tampil? Hanya untuk akun AKTIF yang punya
 * level tujuan tersisa dan tak sedang menunggu verifikasi pengajuan.
 */
export function shouldShowMitraInvite(options: MitraOptions | null): boolean {
  if (!options) return false;
  if (options.customerStatus !== "active") return false;
  if (options.pendingApplication) return false;
  return eligibleUpgradeLevels(options).length > 0;
}

export function mitraApplicationStatusLabel(
  status: MitraApplicationStatus | string
): string {
  switch (status) {
    case "pending":
      return "Menunggu dikonfirmasi";
    case "approved":
      return "Disetujui";
    case "rejected":
      return "Ditolak";
    case "cancelled":
      return "Dibatalkan";
    default:
      // Status tak dikenal (data masa depan) → tampilkan apa adanya, jangan
      // undefined di layar.
      return status;
  }
}

/**
 * Pesan WhatsApp ke admin setelah mengajukan pendaftaran berbayar — cermin
 * `buildTopupWaMessage` (payPage.ts) tapi menyebut LEVEL yang didaftar supaya
 * admin langsung paham approve top-up ini = menaikkan level.
 */
export function buildMitraWaMessage(
  input: {
    customerName: string;
    levelName: string;
    amountLabel: string;
    methodLabel: string;
    reviewUrl: string | null;
  },
  overrides?: WaTemplateOverrides | null
): string {
  return renderWaTemplate(resolveWaTemplate("mitra_daftar", overrides), {
    "[nama pelanggan]": input.customerName.trim() || "saya",
    "[nama level]": input.levelName,
    "[nominal]": input.amountLabel,
    "[via metode]": input.methodLabel ? ` via ${input.methodLabel}` : "",
    "[baris verifikasi]": input.reviewUrl
      ? `Cek & verifikasi di sini: ${input.reviewUrl}`
      : ""
  });
}

/**
 * Kunci localStorage id pengajuan yang SUDAH dirayakan popup "Selamat jadi
 * mitra". Menyimpan id pengajuan TERAKHIR yang dirayakan — pengajuan approved
 * yang lebih baru (naik level lagi) dirayakan lagi, yang sama tidak pernah
 * dua kali (per perangkat).
 */
export const MITRA_CONGRATS_KEY = "galea-mitra-congrats";

/**
 * Popup selamat perlu tampil? Hanya untuk pengajuan approved yang BELUM
 * pernah dirayakan di perangkat ini.
 */
export function shouldCelebrateApplication(
  approvedApplicationId: string | null,
  celebratedId: string | null
): boolean {
  if (!approvedApplicationId) return false;
  return approvedApplicationId !== celebratedId;
}

/** Kunci localStorage stempel tutup banner beranda (idiom galea-*). */
export const MITRA_BANNER_DISMISS_KEY = "galea-mitra-banner-dismissed";
const MITRA_BANNER_DISMISS_MS = 3 * 24 * 60 * 60 * 1000; // 3 hari

/**
 * Banner beranda masih disembunyikan oleh stempel tutup? Stempel rusak/kosong
 * dianggap TIDAK menutup (banner tampil) — kegagalan parse bukan alasan
 * menyembunyikan ajakan selamanya.
 */
export function mitraBannerVisible(
  dismissedAtIso: string | null,
  now: Date
): boolean {
  if (!dismissedAtIso) return true;
  const t = Date.parse(dismissedAtIso);
  if (!Number.isFinite(t)) return true;
  return now.getTime() - t >= MITRA_BANNER_DISMISS_MS;
}

// ─── Pilihan level di /daftar (mig 6180) ────────────────────────────────────
//
// Dua langkah: (1) /daftar — pembeli BELUM login memilih "daftar sebagai"
// Customer Umum atau level mitra, akun dibuat sebagai Customer Umum aktif;
// (2) lanjutan ber-sesi — Customer Umum → /account/alamat (alamat utama
// wajib), level mitra → /mitra (alamat + top-up, alur mig 3810 apa adanya).
// Dua langkah karena pemilih kecamatan, rekening toko, upload bukti, dan
// apply_reseller_upgrade semuanya butuh sesi.

/** Penanda query "datang dari /daftar" di halaman lanjutan. */
export const REGISTRATION_ENTRY_PARAM = "dari";
export const REGISTRATION_ENTRY_VALUE = "daftar";
/** Query id level pilihan langkah 1 di /mitra. */
export const REGISTRATION_LEVEL_PARAM = "level";

/**
 * Koersi defensif hasil RPC `registration_levels_public` (jsonb array). Semua
 * level yang dipulangkan RPC memang menerima pendaftaran, jadi
 * `registrationEnabled` dipaku true. Bentuk aneh → [] (pilihan level hilang,
 * /daftar tampil seperti biasa — fail-closed).
 */
export function mapRegistrationLevels(raw: unknown): MitraLevelOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
    .map((l) => ({
      groupId: str(l.groupId) ?? "",
      slot: Math.trunc(num(l.slot)),
      name: str(l.name) ?? `Level ${Math.trunc(num(l.slot))}`,
      discountPersen: num(l.discountPersen),
      registrationEnabled: true,
      minTopup: Math.max(0, Math.trunc(num(l.minTopup))),
    }))
    .filter((l) => l.groupId !== "" && l.slot > 0)
    .sort((a, b) => a.slot - b.slot);
}

/**
 * Pilihan level di /daftar tampil? Hanya bila akun baru langsung AKTIF —
 * akun pending tak bisa top-up (apply_reseller_upgrade menolak non-active),
 * jadi menawarkan level kepadanya = janji yang tak bisa ditepati.
 */
export function registrationLevelChoices(
  levels: MitraLevelOption[],
  autoActive: boolean
): MitraLevelOption[] {
  return autoActive ? levels : [];
}

/**
 * Halaman lanjutan sesudah akun dibuat & login otomatis. `groupId` null =
 * Customer Umum (isi alamat utama); selain itu /mitra dengan level terpilih.
 */
export function registrationContinueUrl(groupId: string | null): string {
  const entry = `${REGISTRATION_ENTRY_PARAM}=${REGISTRATION_ENTRY_VALUE}`;
  if (!groupId) return `/account/alamat?${entry}`;
  return `/mitra?${REGISTRATION_LEVEL_PARAM}=${encodeURIComponent(groupId)}&${entry}`;
}

/** Baca query halaman lanjutan (nilai searchParams Next: string | string[]). */
export function parseRegistrationEntry(params: {
  [key: string]: string | string[] | undefined;
}): { fromRegistration: boolean; preferredGroupId: string | null } {
  const first = (v: string | string[] | undefined) =>
    Array.isArray(v) ? v[0] : v;
  const level = first(params[REGISTRATION_LEVEL_PARAM])?.trim() ?? "";
  return {
    fromRegistration:
      first(params[REGISTRATION_ENTRY_PARAM]) === REGISTRATION_ENTRY_VALUE,
    // Hanya bentuk uuid — nilai lain tak pernah diteruskan ke mana pun.
    preferredGroupId: /^[0-9a-f-]{36}$/i.test(level) ? level : null,
  };
}

// ─── Statistik konversi mitra (RPC admin_mitra_stats, mig 3820) ──────────────

export type MitraStats = {
  /** Pendaftar mandiri /daftar (customers.self_registered) di rentang. */
  registrationsCur: number;
  registrationsPrev: number;
  /** Pengajuan mitra masuk (gratis + berbayar). */
  applicationsCur: number;
  applicationsPrev: number;
  /** Pengajuan disetujui (atribusi tanggal keputusan). */
  joinedCur: number;
  joinedPrev: number;
};

/** Koersi defensif hasil RPC `admin_mitra_stats` (jsonb). */
export function mapMitraStats(raw: unknown): MitraStats | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const count = (v: unknown) => Math.max(0, Math.trunc(num(v)));
  return {
    registrationsCur: count(o.registrations_cur),
    registrationsPrev: count(o.registrations_prev),
    applicationsCur: count(o.applications_cur),
    applicationsPrev: count(o.applications_prev),
    joinedCur: count(o.joined_cur),
    joinedPrev: count(o.joined_prev),
  };
}

/**
 * Rasio konversi "pendaftar baru → jadi mitra" (0..∞; BOLEH > 1 karena yang
 * jadi mitra bisa pelanggan LAMA, bukan pendaftar rentang ini). Pendaftar 0 →
 * null (rasio atas nol tak bermakna — jangan render 0% yang terbaca "gagal
 * total").
 */
export function mitraConversionRate(
  stats: Pick<MitraStats, "registrationsCur" | "joinedCur">
): number | null {
  if (stats.registrationsCur <= 0) return null;
  return stats.joinedCur / stats.registrationsCur;
}

/** Label persen konversi utk kartu dasbor ("38%"); null = "—". */
export function mitraConversionLabel(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 100)}%`;
}
